import React from 'react';
import { Switch } from 'react-router-dom';
import Loader from '@/components/common/Loader';
import AuthRoutes from '@/constants/routing/AuthRoutes';
import PublicRoutes from '@/constants/routing/PublicRoutes';
import lazyWithRetry from '@/utils/lazyWithRetry';
import AuthRoute from './AuthRoute';
import PrivateRoute from './PrivateRoute';

const AdminBulkImportPage = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "admin-bulk-import-page" */ '@/pages/SuperAdminPages/AdminBulkImportPage'
  ),
);
const AdminLandingPage = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "admin-landing-page" */ '@/pages/AuthPages/AdminLandingPage'
  ),
);
const AdminLoginPage = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "admin-login-page" */ '@/pages/AuthPages/LoginPages/AdminLoginPage'
  ),
);
const AdminRegistrationPage = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "admin-registration-page" */ '@/pages/AuthPages/RegistrationPages/AdminRegistrationPage'
  ),
);
const CampaignPage = lazyWithRetry(() =>
  import(/* webpackChunkName: "campaign-page" */ '@/pages/CampaignPage'),
);
const CampaignsPage = lazyWithRetry(() =>
  import(/* webpackChunkName: "campaigns-page" */ '@/pages/CampaignsPage'),
);
const CompanyPage = lazyWithRetry(() =>
  import(/* webpackChunkName: "company-page" */ '@/pages/CompanyPage'),
);
const ConstituentPage = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "constituent-page" */ '@/pages/ConstituentPage/ConstituentPage'
  ),
);
const ConstituentsPage = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "constituents-page" */ '@/pages/ConstituentsPage/ConstituentsPage'
  ),
);
const HomePage = lazyWithRetry(() =>
  import(/* webpackChunkName: "home-page" */ '@/pages/HomePage'),
);
const IntegrationPage = lazyWithRetry(() =>
  import(/* webpackChunkName: "integration-page" */ '@/pages/IntegrationPage'),
);
const IntegrationsPage = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "integrations-page" */ '@/pages/IntegrationsPage'
  ),
);
const IWaveAuthCallbackPage = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "iwave-auth-callback" */ '@/pages/IWaveAuth/IWaveAuthCallbackPage'
  ),
);
const LifecycleBoard = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "lifecycle-board-page" */ '@/modules/LifecycleBoard'
  ),
);
const LifecycleBoardListView = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "lifecyle-board-list-view-page" */ '@/modules/LifecycleBoard/components/LifecycleBoardListView'
  ),
);
const LoginLinkExpiredPage = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "login-link-expired-page" */ '@/pages/AuthPages/LoginPages/LoginLinkExpiredPage'
  ),
);
const LoginNeedsVerificationPage = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "login-needs-verification-page" */ '@/pages/AuthPages/LoginPages/LoginNeedsVerificationPage'
  ),
);
const LoginPage = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "login-page" */ '@/pages/AuthPages/LoginPages/LoginPage'
  ),
);
const LoginVerificationEmailErrorPage = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "login-verification-email-error-page" */ '@/pages/AuthPages/LoginPages/LoginVerificationEmailErrorPage'
  ),
);
const NotFoundErrorPageRaw = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "not-found-error-page" */ '@/pages/ErrorPages/NotFoundErrorPageRaw'
  ),
);
const OpportunitiesPage = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "opportunities-page" */
    '@/pages/OpportunitiesPage/OpportunitiesPage'
  ),
);
const RegistrationByInvitePage = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "registration-by-invite-page" */ '@/pages/AuthPages/RegistrationPages/RegistrationByInvitePage'
  ),
);
const RegistrationInviteVerificationErrorPage = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "registration-invite-verification-error-page" */
    '@/pages/AuthPages/RegistrationPages/RegistrationInviteVerificationErrorPage'
  ),
);
const RegistrationSuccessPage = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "registration-success-page" */ '@/pages/AuthPages/RegistrationPages/RegistrationSuccessPage'
  ),
);
const RegistrationVerificationErrorPage = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "registration-verification-error-page" */ '@/pages/AuthPages/RegistrationPages/RegistrationVerificationErrorPage'
  ),
);
const ReportsPage = lazyWithRetry(() =>
  import(/* webpackChunkName: "reports-page" */ '@/modules/Reports'),
);
const ResetPasswordPage = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "reset-password-page" */ '@/pages/AuthPages/ForgotPasswordPages/ResetPasswordPage'
  ),
);
const ResetPasswordRequestSuccessPage = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "reset-password-request-success-page" */ '@/pages/AuthPages/ForgotPasswordPages/ResetPasswordRequestSuccessPage'
  ),
);
const ResetPasswordSetPasswordPage = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "reset-password-set-password-page" */ '@/pages/AuthPages/ForgotPasswordPages/ResetPasswordSetPasswordPage'
  ),
);
const ResetPasswordSetPasswordSuccessPage = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "reset-password-set-password-success-page" */
    '@/pages/AuthPages/ForgotPasswordPages/ResetPasswordSetPasswordSuccessPage'
  ),
);
const SalesforceIntegrationPage = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "salesforce-integration-page" */
    '@/pages/IntegrationPage/SalesforceIntegrationPage'
  ),
);
const SuperAdminHomePage = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "super-admin-home-page" */
    '@/pages/SuperAdminPages/SuperAdminHomePage'
  ),
);
const TasksPage = lazyWithRetry(() =>
  import(/* webpackChunkName: "tasks-page" */ '@/pages/TasksPage/TasksPage'),
);
const UserHomePage = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "user-home-page" */ '@/pages/UserPage/UserHomePage'
  ),
);

const {
  ADMIN_LANDING,
  ADMIN_LOGIN,
  ADMIN_REGISTER,
  LOGIN,
  LOGIN_EXPIRED,
  REGISTRATION_FROM_INVITE,
  REGISTRATION_INVITE_VERIFICATION_ERROR,
  REGISTRATION_SUCCESS,
  REGISTRATION_VERIFICATION_ERROR,
  RESET_PASSWORD,
  RESET_PASSWORD_REQUEST_SUCCESS,
  RESET_PASSWORD_SET_PASSWORD,
  RESET_PASSWORD_SET_PASSWORD_SUCCESS,
  VERIFICATION,
  VERIFICATION_ERROR,
} = PublicRoutes;

const {
  ADMIN,
  ADMIN_BULK_IMPORT,
  BOARD,
  BOARDS,
  CAMPAIGN,
  CAMPAIGNS,
  CAMPAIGN_DONATIONS,
  CAMPAIGN_EVENT,
  CAMPAIGN_EVENTS,
  CAMPAIGN_FEED,
  COMPANY,
  COMPANY_ADMIN,
  COMPANY_AUTOMATIONS,
  COMPANY_CUSTOM_FIELDS,
  COMPANY_CONFIDENCE_LEVELS,
  COMPANY_DATA_UPLOADS,
  COMPANY_DATA_UPLOADS_NEW,
  COMPANY_DATA_UPLOADS_MATCH_HEADERS,
  COMPANY_DATA_UPLOADS_REVIEW,
  COMPANY_INVITES,
  COMPANY_LABELS,
  COMPANY_TEAM,
  CONSTITUENT,
  CONSTITUENTS,
  CONSTITUENT_DONATIONS,
  CONSTITUENT_EDIT,
  CONSTITUENT_HISTORY,
  CONSTITUENT_INFO,
  CONSTITUENT_IWAVE,
  CONSTITUENT_NEW,
  CONSTITUENT_TASKS,
  DONORS,
  FEED,
  INTEGRATION,
  INTEGRATIONS,
  IWAVE_AUTH_CALLBACK,
  LANDING,
  OPPORTUNITIES,
  REPORTS,
  SALESFORCE_INTEGRATION,
  SALESFORCE_INTEGRATION_DEFINE_FILTERS,
  SALESFORCE_INTEGRATION_REVIEW_IMPORT,
  TASK,
  TASKS,
  USER,
} = AuthRoutes;

function Router() {
  return (
    <React.Suspense
      fallback={<Loader isCentered className="h-screen items-center" />}
    >
      <Switch>
        <PrivateRoute exact path={LANDING} component={HomePage} />
        <PrivateRoute
          exact
          path={[
            COMPANY,
            COMPANY_ADMIN,
            COMPANY_AUTOMATIONS,
            COMPANY_CUSTOM_FIELDS,
            COMPANY_CONFIDENCE_LEVELS,
            COMPANY_DATA_UPLOADS,
            COMPANY_DATA_UPLOADS_NEW,
            COMPANY_DATA_UPLOADS_MATCH_HEADERS,
            COMPANY_DATA_UPLOADS_REVIEW,
            COMPANY_INVITES,
            COMPANY_LABELS,
            COMPANY_TEAM,
          ]}
          component={CompanyPage}
        />
        <PrivateRoute path={FEED} component={HomePage} />
        <PrivateRoute
          exact
          path={[BOARDS, DONORS]}
          component={LifecycleBoardListView}
        />
        <PrivateRoute path={BOARD} component={LifecycleBoard} />
        <PrivateRoute
          exact
          path={[
            CONSTITUENT_NEW,
            CONSTITUENT,
            CONSTITUENT_EDIT,
            CONSTITUENT_HISTORY,
            CONSTITUENT_INFO,
            CONSTITUENT_IWAVE,
            CONSTITUENT_DONATIONS,
            CONSTITUENT_TASKS,
          ]}
          component={ConstituentPage}
        />
        <PrivateRoute exact path={CAMPAIGNS} component={CampaignsPage} />
        <PrivateRoute
          exact
          path={[
            CAMPAIGN,
            CAMPAIGN_FEED,
            CAMPAIGN_DONATIONS,
            CAMPAIGN_EVENTS,
            CAMPAIGN_EVENT,
          ]}
          component={CampaignPage}
        />
        <PrivateRoute exact path={CONSTITUENTS} component={ConstituentsPage} />
        <PrivateRoute exact path={INTEGRATIONS} component={IntegrationsPage} />
        <PrivateRoute exact path={INTEGRATION} component={IntegrationPage} />
        <PrivateRoute
          exact
          path={[
            SALESFORCE_INTEGRATION,
            SALESFORCE_INTEGRATION_DEFINE_FILTERS,
            SALESFORCE_INTEGRATION_REVIEW_IMPORT,
          ]}
          component={SalesforceIntegrationPage}
        />
        <PrivateRoute path={USER} component={UserHomePage} />
        <PrivateRoute
          path={ADMIN_BULK_IMPORT}
          component={AdminBulkImportPage}
        />
        <PrivateRoute path={ADMIN} component={SuperAdminHomePage} />
        <PrivateRoute exact path={REPORTS} component={ReportsPage} />
        <PrivateRoute path={OPPORTUNITIES} component={OpportunitiesPage} />
        <PrivateRoute path={[TASK, TASKS]} component={TasksPage} />
        <PrivateRoute
          exact
          path={IWAVE_AUTH_CALLBACK}
          component={IWaveAuthCallbackPage}
        />
        <AuthRoute exact path={LOGIN} component={LoginPage} />
        <AuthRoute exact path={ADMIN_LOGIN} component={AdminLoginPage} />
        <AuthRoute path={LOGIN_EXPIRED} component={LoginLinkExpiredPage} />
        <AuthRoute
          exact
          path={VERIFICATION}
          component={LoginNeedsVerificationPage}
        />
        <AuthRoute
          path={VERIFICATION_ERROR}
          component={LoginVerificationEmailErrorPage}
        />
        <AuthRoute exact path={ADMIN_LANDING} component={AdminLandingPage} />
        <AuthRoute
          exact
          path={ADMIN_REGISTER}
          component={AdminRegistrationPage}
        />
        <AuthRoute
          path={REGISTRATION_SUCCESS}
          component={RegistrationSuccessPage}
        />
        <AuthRoute
          path={REGISTRATION_VERIFICATION_ERROR}
          component={RegistrationVerificationErrorPage}
        />
        <AuthRoute
          path={REGISTRATION_INVITE_VERIFICATION_ERROR}
          component={RegistrationInviteVerificationErrorPage}
        />
        <AuthRoute
          path={REGISTRATION_FROM_INVITE}
          component={RegistrationByInvitePage}
        />
        <AuthRoute exact path={RESET_PASSWORD} component={ResetPasswordPage} />
        <AuthRoute
          path={RESET_PASSWORD_REQUEST_SUCCESS}
          component={ResetPasswordRequestSuccessPage}
        />
        <AuthRoute
          exact
          path={RESET_PASSWORD_SET_PASSWORD}
          component={ResetPasswordSetPasswordPage}
        />
        <AuthRoute
          path={RESET_PASSWORD_SET_PASSWORD_SUCCESS}
          component={ResetPasswordSetPasswordSuccessPage}
        />
        <PrivateRoute component={NotFoundErrorPageRaw} />
      </Switch>
    </React.Suspense>
  );
}

export default Router;
